import React, { useEffect, useRef } from "react";
import { NavLink, useLocation } from "react-router-dom";
import Icon from "./Icon";
import "./Tabs.scss";

// Fontaine M23 — Tabs. Visual contract: Style Guide V2 `tab-default / tab-hover / tab-active` (pill tab)
// plus an `underline` variant for page sections. Two modes: routing (items with `to` → NavLink) or
// controlled (`value` + `onChange`, role=tablist). Scrolls on phones and keeps the active tab in view.
// Locked tabs (entitlements) render with a lock and a reason instead of navigating.

/**
 * @typedef {{ label: ReactNode, to?: string, value?: string, exact?: boolean, icon?: ReactNode, count?: number, countTone?: "alert", countLabel?: string, locked?: boolean, lockedReason?: string, disabled?: boolean }} TabItem
 *
 * @param {TabItem[]} items
 * @param {string} [value]                     controlled mode: selected item.value
 * @param {(value: string, item) => void} [onChange]
 * @param {string} [activePath]                routing mode: force the active path (defaults to the location)
 * @param {"pill"|"underline"} [variant="pill"]
 * @param {"sm"|"md"} [size="md"]              32 · 40
 * @param {boolean} [fullWidth]                tabs share the width (phone bottom sections)
 * @param {(item) => void} [onLocked]          tap on a locked tab (open the upgrade sheet)
 */
export default function Tabs({ items = [], value, onChange, activePath, variant = "pill", size = "md", fullWidth = false, onLocked, className = "", "aria-label": ariaLabel = "Tabs", ...rest }) {
  const { pathname } = useLocation();
  const listRef = useRef(null);
  const controlled = value !== undefined || typeof onChange === "function";

  const isActive = (item) => {
    if (controlled) return item.value === value;
    if (activePath != null) return item.to === activePath;
    if (!item.to) return false;
    if (item.exact) return pathname === item.to;
    return pathname === item.to || pathname.startsWith(`${item.to}/`);
  };

  // keep the active tab visible when the strip scrolls (phones)
  useEffect(() => {
    const el = listRef.current?.querySelector(".is-active");
    if (el && listRef.current && listRef.current.scrollWidth > listRef.current.clientWidth) {
      el.scrollIntoView({ block: "nearest", inline: "center" });
    }
  }, [value, pathname]);

  const enabled = items.filter((i) => !i.disabled);
  const onKeyDown = (e) => {
    if (!controlled || !enabled.length) return;
    const idx = Math.max(0, enabled.findIndex((i) => i.value === value));
    let next;
    if (e.key === "ArrowRight") next = enabled[(idx + 1) % enabled.length];
    else if (e.key === "ArrowLeft") next = enabled[(idx - 1 + enabled.length) % enabled.length];
    else if (e.key === "Home") next = enabled[0];
    else if (e.key === "End") next = enabled[enabled.length - 1];
    if (!next) return;
    e.preventDefault();
    if (next.locked) onLocked?.(next);
    else onChange?.(next.value, next);
    listRef.current?.querySelector(`[data-value="${next.value}"]`)?.focus();
  };

  const classes = ["ft-tabs", `ft-tabs--${variant === "underline" ? "underline" : "pill"}`, `ft-tabs--${size === "sm" ? "sm" : "md"}`, fullWidth ? "ft-tabs--full" : "", className].filter(Boolean).join(" ");

  const content = (item, active) => (
    <>
      {/* Tab icons are intentionally not rendered — label-only tab rows read
          cleaner under a hero title (Guillaume, 2026-08-31). The `icon` field is
          still accepted across PageHeader and every tab constant, so restoring
          them is this one line. */}
      <span className="ft-tabs__label">{item.label}</span>
      {/* countTone "alert" = the red pill (pending actions, Stock Alert); the
          default stays the neutral count every other page uses. */}
      {typeof item.count === "number" ? (
        <span
          className={`ft-tabs__count${item.countTone === "alert" ? " ft-tabs__count--alert" : ""}`}
          aria-label={item.countLabel}
        >
          {item.count}
        </span>
      ) : null}
      {item.locked ? <span className="ft-tabs__lock" aria-hidden="true"><Icon name="Lock" size="xs" /></span> : null}
      {active && variant === "underline" ? <span className="ft-tabs__ink" aria-hidden="true" /> : null}
    </>
  );

  if (!controlled) {
    return (
      <nav className={classes} aria-label={ariaLabel} ref={listRef} {...rest}>
        {items.map((item) => {
          const active = isActive(item);
          const cls = `ft-tabs__tab${active ? " is-active" : ""}${item.locked ? " is-locked" : ""}${item.disabled ? " is-disabled" : ""}`;
          if (item.locked || item.disabled || !item.to) {
            return (
              <button key={item.value || item.to || String(item.label)} type="button" className={cls} disabled={item.disabled} aria-disabled={item.locked || undefined} title={item.lockedReason} onClick={() => item.locked && onLocked?.(item)}>
                {content(item, active)}
              </button>
            );
          }
          return (
            <NavLink key={item.to} to={item.to} exact={!!item.exact} className={cls} activeClassName="is-active" aria-current={active ? "page" : undefined}>
              {content(item, active)}
            </NavLink>
          );
        })}
      </nav>
    );
  }

  return (
    <div className={classes} role="tablist" aria-label={ariaLabel} ref={listRef} onKeyDown={onKeyDown} {...rest}>
      {items.map((item) => {
        const active = isActive(item);
        return (
          <button
            key={item.value}
            type="button"
            role="tab"
            data-value={item.value}
            aria-selected={active}
            aria-disabled={item.locked || undefined}
            tabIndex={active ? 0 : -1}
            disabled={item.disabled}
            title={item.lockedReason}
            className={`ft-tabs__tab${active ? " is-active" : ""}${item.locked ? " is-locked" : ""}`}
            onClick={() => (item.locked ? onLocked?.(item) : onChange?.(item.value, item))}
          >
            {content(item, active)}
          </button>
        );
      })}
    </div>
  );
}
